import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Button from '../../library/button/button';
import Link from '../../library/link/link';
import StarRating, { RatingLink } from '../../library/star-rating/star-rating';
import PhoneNumber from '../phone-number/phone-number';
import PitPassLogo from '../pitpass-logo/pitpass-logo';
import StoreFeaturedServices from '../store-featured-services/store-featured-services';

import {
  useModal,
  useOptimisticData,
  useScheduleAppointment,
  useScrollTo
} from '../../shared/hooks/hooks';

import { getStoreDetailsUrl } from '../../shared/utils/store/store';

import { SHARE_STORE } from '../../shared/constants/modals';

import './mini-store.scss';

function MiniStore(props) {
  const {
    onChangeStoreClick,
    onReadReviewsClick,
    onScheduleAppointmentClick,
    onServiceLinkClick,
    onStoreDetailsClick,
    store
  } = props;
  const { openModal } = useModal();

  const { handleScheduleAppointment } = useScheduleAppointment();
  const optimisticData = useOptimisticData();
  const {
    address = {},
    code,
    displayName,
    legacyStoreCode,
    pitPass: isPitPassStore = false,
    rating
  } = store || {};

  const {
    phone,
    postalCode,
    region: { isocodeShort: regionCode } = {}
  } = address;
  const line1 =
    typeof address.line1 === 'string' ? address.line1.toLowerCase() : '';
  const city =
    typeof address.town === 'string' ? address.town.toLowerCase() : '';
  const state = typeof regionCode === 'string' ? regionCode.toLowerCase() : '';
  const storeUrl = getStoreDetailsUrl(address, code);
  const containerRef = useRef(null);
  const containerRefCurrent = containerRef?.current;

  const scrollToReviews = useScrollTo();

  useEffect(() => {
    if (containerRefCurrent) {
      containerRefCurrent?.focus();
    }
  }, [containerRefCurrent]);

  if (!store?.code) {
    return null;
  }

  return (
    <div
      data-testid="mini-store"
      ref={containerRef}
      styleName="container"
      tabIndex="-1"
    >
      <div styleName="heading">
        <div>
          <strong>{displayName}</strong>
          <div styleName="review">
            {Number.isFinite(rating?.rating) && (
              <StarRating rating={rating.rating} />
            )}
            {Number.isInteger(rating?.numberOfReviews) && (
              <span styleName="rating-link">
                <RatingLink
                  count={rating.numberOfReviews}
                  href={`${storeUrl}#read-reviews`}
                  onClick={() => {
                    scrollToReviews({
                      elem: document.getElementById('read-reviews'),
                      offset: 100
                    });
                    onReadReviewsClick();
                  }}
                />
              </span>
            )}
          </div>
        </div>

        {isPitPassStore && (
          <>
            <PitPassLogo isPitPassLogoOnly />
            <Link to="/pitpass">Learn more</Link>
          </>
        )}
      </div>
      <div styleName="info">
        {line1 && city && state && postalCode && phone && (
          <div styleName="address">
            {line1}
            <br />
            {`${city}, ${regionCode} `}
            {postalCode}
            <PhoneNumber isUnformatted phone={phone} styleName="phone" />

            {code && legacyStoreCode && (
              <Button
                onClick={() => {
                  openModal(SHARE_STORE, {
                    store
                  });
                }}
              >
                Send to phone
              </Button>
            )}
          </div>
        )}
        <div styleName="hours">
          <span styleName="hours-heading">Store Hours</span>
          <ul styleName="hours-list">
            <li styleName="hours-list-item">
              <span styleName="day">Mon - Fri</span>&nbsp;8AM - 6PM
            </li>
            <li styleName="hours-list-item">
              <span styleName="day">Saturday</span>&nbsp;8AM - 5PM
            </li>
            <li styleName="hours-list-item">
              <span styleName="day">Sunday</span>&nbsp;Closed
            </li>
          </ul>
        </div>
      </div>

      <StoreFeaturedServices
        myStore={store}
        onServiceLinkClick={onServiceLinkClick}
        showLinks
        styleName="store-featured-services"
      />

      <div styleName="actions">
        <Button href="/store-locator" isTertiary onClick={onChangeStoreClick}>
          Change Store
        </Button>
        <Button
          href={storeUrl}
          isTertiary
          onClick={() => {
            optimisticData.write({
              store
            });
            onStoreDetailsClick();
          }}
        >
          Store Details
        </Button>
        <Button
          isTertiary
          onClick={() => {
            handleScheduleAppointment();
            onScheduleAppointmentClick();
          }}
        >
          Schedule Appointment
        </Button>
      </div>
    </div>
  );
}

MiniStore.propTypes = {
  onChangeStoreClick: PropTypes.func,
  onReadReviewsClick: PropTypes.func,
  onScheduleAppointmentClick: PropTypes.func,
  onServiceLinkClick: PropTypes.func,
  onStoreDetailsClick: PropTypes.func,
  store: PropTypes.shape({
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      phone: PropTypes.string,
      postalCode: PropTypes.string,
      region: PropTypes.shape({
        isocodeShort: PropTypes.string
      }),
      town: PropTypes.string
    }).isRequired,
    code: PropTypes.string,
    description: PropTypes.string,
    displayName: PropTypes.string,
    legacyStoreCode: PropTypes.string,
    pitPass: PropTypes.bool,
    rating: PropTypes.shape({
      numberOfReviews: PropTypes.number,
      rating: PropTypes.number
    })
  })
};

export default MiniStore;
